import { Suspense } from 'react';
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
  redirect,
} from 'react-router-dom';

import { LayoutDefault } from 'app/layouts/LayoutDefault/LayoutDefault';

import { MedOrgDetailsPage, MedOrgsPage } from 'pages/MedOrg';
import {
  MedOrgInfoTab,
  MedicalDeviceTypeDetailsPage,
  MedicalDevicesTypesPage,
} from 'pages/MedicalDevicesTypes';
import { StatsPage } from 'pages/Stats';
import { UsersPage } from 'pages/User/UsersPage';
import { ErrorPage } from 'pages/error';
import { LoginPage } from 'pages/login/LoginPage';
import { ResetPasswordPage } from 'pages/login/ResetPasswordPage';
import { SetPasswordPage } from 'pages/login/SetPasswordPage';
import { MainPage } from 'pages/main';
import { NotFound } from 'pages/notFound';
import { NotImplemented } from 'pages/notImplemented';

import { PageLoader } from 'widgets/PageLoader';

import { LoginResponse } from 'shared/api/__generated__';
import { Loader } from 'shared/ui/Loader';
import { NoAccessCover } from 'shared/ui/NoAccessCover';
import { PageTitle } from 'shared/ui/PageTitle';

function makeRouter() {
  return createBrowserRouter([
    {
      path: '/reset-password',
      loader: () => {
        localStorage.clear();
        return null;
      },
      element: <ResetPasswordPage />,
    },
    {
      path: '/set-password/:resetPasswordId',
      loader: () => {
        localStorage.clear();
        return null;
      },
      errorElement: <ErrorPage />,
      element: <SetPasswordPage />,
    },
    {
      path: '/login',
      loader: () => {
        const isAuthorized = !!localStorage.getItem('USER_DATA');
        if (isAuthorized) return redirect('/');
        return null;
      },
      element: <LoginPage />,
    },
    {
      path: '/login-success',
      loader: () => {
        const userData = localStorage.getItem('USER_DATA');

        if (!userData) return redirect('/login');

        const user: LoginResponse = JSON.parse(userData);

        // Если админ ЕРП - на список медорганизаций
        if (user.role?.key === 'admin.erp') {
          return redirect('/manage/medical_orgs');
        }

        // Если админ медорганизации - на страницу его медорганизации
        if (user.role?.key === 'admin.mo' && user.medorg?.id) {
          return redirect(`/manage/medical_orgs/${user.medorg.id}`);
        }

        // Иначе, как обычно
        return redirect('/');
      },
    },
    // Корневые и системные страницы должны быть ниже всех в иерархии
    {
      path: '/',
      loader: () => {
        const isAuthorized = !!localStorage.getItem('USER_DATA');
        if (!isAuthorized) return redirect('/login');
        return null;
      },
      errorElement: <ErrorPage />,
      element: <LayoutDefault />,
      children: [
        {
          path: '',
          element: (
            <>
              <PageTitle title='ЕРП - Главная' />
              <MainPage />
            </>
          ),
        },
        {
          path: 'manage',
          children: [
            {
              path: 'no-access',
              element: <NoAccessCover />,
            },
            {
              path: 'medical_orgs',
              element: <MedOrgsPage title='Медорганизации' />,
            },
            {
              path: 'medical_org/:id',
              element: <NotImplemented title='Медорганизация' />,
            },
            {
              path: 'users',
              element: <UsersPage title='Пользователи' />,
            },
            {
              path: 'medical_devices_types',
              element: (
                <MedicalDevicesTypesPage title='Типы медицинских изделий' />
              ),
            },
            {
              path: 'medical_devices_types/:id',
              element: <MedicalDeviceTypeDetailsPage />,
            },

            {
              path: 'medical_orgs/:id',
              element: <MedOrgDetailsPage />,
              children: [
                {
                  path: 'info',
                  element: <MedOrgInfoTab />,
                },
                {
                  index: true,
                  element: <Navigate to='info' replace />,
                },
              ],
            },
            {
              path: 'medical_devices',
              element: <NotImplemented title='Медицинские изделия' />,
            },

            {
              path: 'markings',
              element: <NotImplemented title='Маркировки' />,
            },
          ],
        },
        {
          path: 'profile',
          element: <NotImplemented title='Профиль' />,
        },
        {
          path: 'settings',
          element: <NotImplemented title='Настройки' />,
        },
        {
          path: 'stats/:id',
          element: <StatsPage />,
        },
        {
          path: 'logout',
          element: <NotImplemented title='Выход' />,
          loader: () => {
            localStorage.clear();
            return redirect('/login');
          },
        },
      ],
    },
    {
      path: '*',
      element: <NotFound />,
    },
  ]);
}

export function AppRouter() {
  return (
    <div className='page-wrapper'>
      <Suspense fallback={<PageLoader />}>
        <RouterProvider
          fallbackElement={<Loader />}
          router={makeRouter()}
          future={{
            v7_startTransition: true,
          }}
        />
      </Suspense>
    </div>
  );
}
